import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Role permissions mapping
const ROLE_PERMISSIONS = {
  admin: {
    pages: ['all'],
    canApproveDiscounts: true,
    branchLocked: false
  },
  manager: {
    pages: ['dashboard', 'reports', 'discounts', 'expenses', 'invoices', 'payments', 'bank_reconciliation', 'fee_structure', 'credit_notes', 'students', 'families', 'settings', 'feedback_tickets'],
    canApproveDiscounts: false,
    canInitiateDiscounts: true,
    canManageStudents: true,
    branchLocked: true
  },
  accountant: {
    pages: ['dashboard', 'invoices', 'payments', 'students', 'credit_notes', 'call_logs', 'discounts', 'bank_reconciliation'],
    canApproveDiscounts: false,
    canInitiateDiscounts: true,
    branchLocked: true
  },
  admission_incharge: {
    pages: ['students'],
    canApproveDiscounts: false,
    branchLocked: true
  },
  accountant_assistant: {
    pages: ['dashboard', 'reports', 'invoices', 'payments', 'bank_reconciliation', 'credit_notes', 'call_logs', 'lunch_coupons', 'club_attendance', 'uniforms', 'students', 'families', 'discounts'],
    canApproveDiscounts: false,
    canInitiateDiscounts: true,
    // Issues credit notes; approving them stays admin-only (maker/checker).
    canInitiateCreditNotes: true,
    branchLocked: true
  },
  field_supervisor: {
    pages: ['lunch_coupons', 'club_attendance'],
    canApproveDiscounts: false,
    branchLocked: true
  }
};

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [permissions, setPermissions] = useState(null);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
      setError(null);
      
      // Set permissions based on role
      const role = response.data?.role || '';
      const rolePerms = ROLE_PERMISSIONS[role] || { pages: [], branchLocked: true };
      setPermissions({
        ...rolePerms,
        role,
        branches: response.data?.branches || (response.data?.branch ? [response.data.branch] : [])
      });
    } catch (err) {
      setUser(null);
      setPermissions(null);
      if (err.response?.status !== 401) {
        const detail = err.response?.data?.detail;
        if (detail === 'License Expired, Contact DWEB') {
          setError(detail);
        }
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      setUser(response.data);
      setError(null);
      
      // Set permissions based on role
      const role = response.data?.role || '';
      const rolePerms = ROLE_PERMISSIONS[role] || { pages: [], branchLocked: true };
      setPermissions({
        ...rolePerms,
        role,
        branches: response.data?.branches || (response.data?.branch ? [response.data.branch] : [])
      });
      
      return { success: true };
    } catch (err) {
      const detail = err.response?.data?.detail;
      let errorMsg = 'Login failed. Please try again.';
      
      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (Array.isArray(detail)) {
        errorMsg = detail.map(e => e.msg || JSON.stringify(e)).join(' ');
      }
      
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setUser(null);
      setPermissions(null);
    }
  };

  // Check if user has access to a specific page
  const hasPageAccess = (page) => {
    if (!permissions) return false;
    if (permissions.pages.includes('all')) return true;
    return permissions.pages.includes(page);
  };

  // Check if user has access to a specific branch
  const hasBranchAccess = (branch) => {
    if (!permissions) return false;
    if (!permissions.branchLocked) return true; // Admin
    return permissions.branches?.includes(branch);
  };

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    checkAuth,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    permissions,
    hasPageAccess,
    hasBranchAccess,
    userBranches: permissions?.branches || [],
    role: user?.role || '',
    roleName: (user?.role || '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
